// ============================================
// CANDIDATE FILTERS COMPONENT
// ============================================

import { Search, Filter, X, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/utils';
import { useMyJobs } from '@/hooks';
import { useTranslation } from '@crew-at-sea/i18n';

interface CandidateFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  department: string;
  onDepartmentChange: (value: string) => void;
  selectedJobId: string;
  onJobChange: (value: string) => void;
  availableOnly: boolean;
  onAvailableChange: (value: boolean) => void;
  onReset: () => void;
}

export function CandidateFilters({
  search,
  onSearchChange,
  department,
  onDepartmentChange,
  selectedJobId,
  onJobChange,
  availableOnly,
  onAvailableChange,
  onReset,
}: CandidateFiltersProps) {
  const { t } = useTranslation(['panel', 'enums']);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Şirketin aktif ilanlarını eşleştirme filtresi için çek
  const { data: jobsData } = useMyJobs({ status: 'ACTIVE', limit: 50 });
  const jobs = (() => {
    if (!jobsData) return [];
    const raw = jobsData as any;
    if (raw.data && Array.isArray(raw.data.data)) return raw.data.data;
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.items)) return raw.items;
    return [];
  })();

  // Departman enum key'leri
  const departmentKeys = ['DECK', 'ENGINE', 'ELECTRICAL', 'CATERING', 'HOTEL', 'OTHER'];

  const hasActiveFilters = department || selectedJobId || availableOnly;

  return (
    <div className="space-y-4">
      {/* Ana filtre satırı */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Arama */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('panel:candidates.searchPlaceholder')}
            className="input pl-10 w-full"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {/* Departman */}
        <select
          className="input w-auto min-w-[160px]"
          value={department}
          onChange={(e) => onDepartmentChange(e.target.value)}
        >
          <option value="">{t('panel:candidates.allDepartments')}</option>
          {departmentKeys.map((key) => (
            <option key={key} value={key}>
              {t(`enums:department.${key}`)}
            </option>
          ))}
        </select>

        {/* İş Eşleştirme Filtresi */}
        <select
          className="input w-auto min-w-[200px]"
          value={selectedJobId}
          onChange={(e) => onJobChange(e.target.value)}
        >
          <option value="">{t('panel:candidates.matchByJob')}</option>
          {jobs.map((job: any) => (
            <option key={job.id} value={job.id}>
              {job.title}
            </option>
          ))}
        </select>

        {/* Gelişmiş filtre toggle */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={cn(
            'btn-secondary inline-flex items-center gap-2',
            showAdvanced && 'bg-maritime-50 text-maritime-600 border-maritime-200'
          )}
        >
          <Filter className="w-4 h-4" />
          {t('panel:candidates.filters')}
          <ChevronDown className={cn('w-4 h-4 transition-transform', showAdvanced && 'rotate-180')} />
        </button>

        {/* Temizle */}
        {hasActiveFilters && (
          <button
            onClick={onReset}
            className="btn-secondary text-red-600 border-red-200 hover:bg-red-50 inline-flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            {t('panel:candidates.clear')}
          </button>
        )}
      </div>

      {/* Gelişmiş filtreler */}
      {showAdvanced && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Sadece müsait checkbox */}
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={availableOnly}
                onChange={(e) => onAvailableChange(e.target.checked)}
                className="w-4 h-4 text-maritime-600 border-gray-300 rounded focus:ring-maritime-500"
              />
              <span className="text-sm text-gray-700">{t('panel:candidates.availableOnly')}</span>
            </label>

            {/* Aktif filtre özeti */}
            {hasActiveFilters && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>{t('panel:candidates.activeFilters')}</span>
                {department && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-maritime-100 text-maritime-700 rounded-full">
                    {t(`enums:department.${department}`)}
                    <button onClick={() => onDepartmentChange('')} className="hover:text-maritime-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {selectedJobId && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full">
                    {t('panel:candidates.jobMatching')}
                    <button onClick={() => onJobChange('')} className="hover:text-green-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {availableOnly && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                    {t('panel:candidates.available')}
                    <button onClick={() => onAvailableChange(false)} className="hover:text-blue-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
